import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { CARE_PLAN_DIALOG_OPTIONS } from '../../care-plan-dialog.options';
import { CarePlanItemCardComponent } from '../../shared/care-plan-item-card/care-plan-item-card.component';
import { CarePlanStatusSelectComponent } from '../../shared/care-plan-status-select/care-plan-status-select.component';
import {
  OptionEditorDialogComponent,
  OptionEditorDialogData,
} from '../option-editor-dialog/option-editor-dialog.component';
import { optionApiErrorMessage } from '../api/option-api-error';
import { CarePlanOption, KNOWN_OPTION_STATUS } from '../options.model';
import { CarePlanOptionsStore } from '../options.store';

@Component({
  selector: 'bn-option-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CarePlanItemCardComponent, CarePlanStatusSelectComponent],
  templateUrl: './option-card.component.html',
  styleUrl: './option-card.component.scss',
})
export class OptionCardComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _messages = inject(MessageService, { optional: true });

  readonly option = input.required<CarePlanOption>();
  readonly store = input.required<CarePlanOptionsStore>();
  readonly readonly = input(false);

  protected readonly displayedStatusCode = linkedSignal(
    () => this.option().statusCode,
  );
  protected readonly typeLabel = computed(() =>
    this.store().optionTypeLabel(this.option().optionTypeCode),
  );
  protected readonly statusLabel = computed(() =>
    this.store().statusLabel(this.option().statusCode),
  );
  protected readonly statusOptions = computed(() =>
    this.store().statusOptionsFor(this.option().statusCode),
  );
  protected readonly canTransition = computed(
    () => !this.readonly() && this.statusOptions().length > 0,
  );
  protected readonly statusClass = computed(
    () =>
      `bn-option-card__status bn-option-card__status--${this.option().statusCode.toLowerCase()}`,
  );

  async onStatusChange(statusCode: string): Promise<void> {
    const option = this.option();
    if (this.readonly() || option.statusCode === statusCode) return;
    this.displayedStatusCode.set(statusCode);
    if (
      statusCode === KNOWN_OPTION_STATUS.SELECTED &&
      this.store().requiresQualification(option.title)
    ) {
      const dialogRef = this.openEdit(statusCode);
      if (!dialogRef) {
        this.displayedStatusCode.set(this.option().statusCode);
        return;
      }
      dialogRef.onClose.pipe(take(1)).subscribe(() => {
        this.displayedStatusCode.set(this.option().statusCode);
      });
      return;
    }
    try {
      await this.store().updateStatus(option.id, statusCode);
    } catch (error) {
      this.displayedStatusCode.set(option.statusCode);
      this._messages?.add({
        severity: 'error',
        summary: "Couldn't update option",
        detail: optionApiErrorMessage(error, 'Try again.'),
      });
    }
  }

  openEdit(initialStatusCode?: string): DynamicDialogRef | null {
    if (this.readonly()) return null;
    return this._dialog.open(OptionEditorDialogComponent, {
      ...CARE_PLAN_DIALOG_OPTIONS,
      width: '42rem',
      breakpoints: { '768px': 'calc(100vw - 2rem)' },
      styleClass: 'bn-option-editor-dialog-host',
      ariaLabelledBy: 'option-editor-dialog-title',
      data: {
        mode: 'edit',
        store: this.store(),
        option: this.option(),
        initialStatusCode,
      } satisfies OptionEditorDialogData,
    });
  }
}
